import { lazy, Suspense, useEffect } from "react";
import {
  createRootRoute,
  Outlet,
  Link,
  retainSearchParams,
} from "@tanstack/react-router";
import { Providers } from "@/app/providers";
import { ToastContainer } from "@/components/ui/toast/toast";
import { capturePendingPrompt } from "@/features/handoff/capture";
import { WorkspaceSearchSync } from "@/features/account/workspace-switch/workspace-search-sync";
import { useCreatePlaybookStore } from "@/features/brand-playbook/create-playbook.store";
import {
  nonEmptySearchString,
  type AccountV2TestUserSearch,
} from "@/lib/account-v2-test-user-search";
import { validateProjectLoginTarget } from "@/features/auth/signup/project-login-target";
import { VideoDevModeBanner } from "@/features/video-dev/VideoDevModeBanner";

const CreatePlaybookModalHost = lazy(() =>
  import("@/features/brand-playbook/components/CreatePlaybookModalHost").then(
    (module) => ({ default: module.CreatePlaybookModalHost }),
  ),
);

export interface RootSearch extends AccountV2TestUserSearch {
  devUser?: string;
  /** Active workspace, retained across every route transition. */
  ws?: string;
  /** Route-owned pane state for canonical workspace-brand detail URLs. */
  tab?: string;
  /** Fully visible brand selected as the parent for the list create overlay. */
  "parent-brand"?: string;
  /** Workspace id an invitee just joined — surfaces the I4 landed banner on
   *  the dashboard. Cleared by the banner's Dismiss action. */
  joined?: string;
  /** Role granted on the workspace they joined — labels the I4 banner copy. */
  role?: "admin" | "member";
  /** Fresh-workspace signal set by `CreateWorkspacePage` post-create redirect.
   *  Pops the A3 welcome starter-kit modal on `/dashboard`. Lives on root
   *  (like `joined`/`role`) so root validation doesn't strip it before the
   *  dashboard route can read it. Cleared by the modal's Dismiss. */
  welcome?: "1";
  /** Generation-mode reveal for the Full / Quick / UX Test navbar control.
   *  Lives on root so route-level validation does not strip it before
   *  `useGenerationModeAccess` can read it. Semantics are owned by
   *  `parseGenerationModeOverride` in `src/lib/feature-flags.ts`; the schema
   *  here mirrors the raw URL contract (`quick`, `ux`, `ux_test`). Retained
   *  across every route transition so the gate stays open after navigation. */
  gm?: "quick" | "ux" | "ux_test";
  /** A project permalink carried only through the login route. */
  inviteWorkspaceId?: string;
  inviteProjectId?: string;
}

const INVITE_ROLES = new Set<RootSearch["role"]>(["admin", "member"]);
const GENERATION_MODE_REVEAL_VALUES = new Set<RootSearch["gm"]>([
  "quick",
  "ux",
  "ux_test",
]);

export const validateRootSearch = (
  search: Record<string, unknown>,
): RootSearch => {
  const devUser = nonEmptySearchString(search.devUser);
  const testUser = nonEmptySearchString(search.testUser);
  const ws = nonEmptySearchString(search.ws);
  const tab = nonEmptySearchString(search.tab);
  const parentBrand = nonEmptySearchString(search["parent-brand"]);
  const joined = nonEmptySearchString(search.joined);
  const roleRaw = nonEmptySearchString(search.role);
  const role =
    roleRaw && INVITE_ROLES.has(roleRaw as RootSearch["role"])
      ? (roleRaw as RootSearch["role"])
      : undefined;
  const gmRaw = nonEmptySearchString(search.gm)?.toLowerCase();
  const gm =
    gmRaw && GENERATION_MODE_REVEAL_VALUES.has(gmRaw as RootSearch["gm"])
      ? (gmRaw as RootSearch["gm"])
      : undefined;
  // Tolerate BOTH the string `'1'` (what call-sites pass to
  // `navigate({ search: { welcome: '1' } })`) and the number `1` (what
  // TSR's default `parseSearchWith(JSON.parse)` returns when the URL is
  // round-tripped through a raw href — e.g. `WorkspaceSearchSync`'s
  // `navigate({ href })` mirror). Without the numeric branch the signal
  // gets silently stripped the first time the URL is re-parsed, which
  // strands the A3 welcome modal and the first-time sidebar branch.
  const welcome =
    search.welcome === "1" || search.welcome === 1
      ? ("1" as const)
      : undefined;
  const projectLoginTarget = validateProjectLoginTarget(search);
  return {
    ...(devUser ? { devUser } : {}),
    ...(testUser ? { testUser } : {}),
    ...(ws ? { ws } : {}),
    ...(tab ? { tab } : {}),
    ...(parentBrand ? { "parent-brand": parentBrand } : {}),
    ...(joined ? { joined } : {}),
    ...(role ? { role } : {}),
    ...(welcome ? { welcome } : {}),
    ...(gm ? { gm } : {}),
    ...(projectLoginTarget ?? {}),
  };
};

export const rootSearchMiddlewares = [
  // `welcome` is retained so a workspace-search-sync or any other silent
  // navigate() that fires between the create-workspace redirect and the modal
  // mount doesn't drop the signal on the floor — the modal explicitly clears
  // it on dismiss.
  retainSearchParams<RootSearch>(["devUser", "testUser", "ws", "welcome", "gm"]),
];

/**
 * Root route layout.
 *
 * Wraps all routes with application-level providers (TanStack Query, etc.).
 * The three-panel shell lives in the _app layout route so that auth pages
 * (login, signup) can render outside it.
 */
export const Route = createRootRoute({
  validateSearch: validateRootSearch,
  search: {
    middlewares: rootSearchMiddlewares,
  },
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  const createPlaybookOpen = useCreatePlaybookStore((state) => state.isOpen);

  // Capture a cross-app prompt handoff (juice_prompt query/cookie) before any
  // route-level auth guard can strip the URL or redirect. See
  // `src/features/handoff/CLAUDE.md`.
  useEffect(() => {
    capturePendingPrompt();
  }, []);

  return (
    <Providers>
      <WorkspaceSearchSync />
      <Outlet />
      <ToastContainer />
      <VideoDevModeBanner />
      {createPlaybookOpen && (
        <Suspense fallback={null}>
          <CreatePlaybookModalHost />
        </Suspense>
      )}
    </Providers>
  );
}

function NotFound() {
  return (
    <div
      data-visual-not-found
      className="flex min-h-screen items-center justify-center bg-background"
    >
      <div className="text-center">
        <p className="text-8xl font-bold text-grey-200">404</p>
        <h1 className="mt-4 text-xl font-semibold text-foreground">
          Page not found
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/dashboard"
          className="mt-6 inline-block rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-grey-800"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
